/* eslint-disable react/jsx-props-no-spreading */
import type { Color } from '@onefootprint/design-tokens';
import type { Icon } from '@onefootprint/icons';
import { cx } from 'class-variance-authority';
import type { AnchorHTMLAttributes, ButtonHTMLAttributes, HTMLAttributeAnchorTarget, ReactElement, Ref } from 'react';
import { forwardRef } from 'react';

import LoadingSpinner from '../loading-spinner';
import type { ButtonSize, ButtonVariant } from './button.types';

type BaseProps = {
  children: string | ReactElement<Icon>;
  className?: string;
  disabled?: boolean;
  fullWidth?: boolean;
  iconColor?: Color;
  loading?: boolean;
  loadingAriaLabel?: string;
  prefixIcon?: Icon;
  suffixIcon?: Icon;
  size?: ButtonSize;
  testID?: string;
  variant?: ButtonVariant;
};

export type ButtonAsButtonProps = BaseProps & {
  href?: never;
  target?: never;
  type?: 'button' | 'submit' | 'reset';
  form?: string;
} & Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'type' | 'form'>;

export type ButtonAsAnchorProps = BaseProps & {
  /** Renders a real anchor, so cmd-click, right-click and hover preview all work. */
  href: string;
  target?: HTMLAttributeAnchorTarget;
  /** Button-only attributes are rejected here: an anchor submits nothing. */
  type?: never;
  form?: never;
} & Omit<AnchorHTMLAttributes<HTMLAnchorElement>, 'href' | 'target' | 'type'>;

export type ButtonProps = ButtonAsButtonProps | ButtonAsAnchorProps;

const Button = forwardRef<HTMLButtonElement | HTMLAnchorElement, ButtonProps>(
  (
    {
      children,
      className,
      disabled = false,
      form,
      fullWidth,
      href,
      iconColor,
      loading = false,
      loadingAriaLabel,
      prefixIcon: PrefixIcon,
      suffixIcon: SuffixIcon,
      size = 'default',
      target,
      testID,
      type = 'button',
      variant = 'primary',
      ...props
    }: ButtonProps,
    ref,
  ) => {
    const defaultIconColor = variant === 'primary' ? 'quinary' : 'primary';
    const getContent = () => (
      <span className={`flex items-center justify-center gap-2 ${loading ? 'invisible' : 'visible'}`}>
        {PrefixIcon && <PrefixIcon color={iconColor || defaultIconColor} />}
        <span className="overflow-hidden text-ellipsis whitespace-nowrap">{children}</span>
        {SuffixIcon && <SuffixIcon color={iconColor || defaultIconColor} />}
      </span>
    );

    const sharedProps = {
      /** Do not change/remove these classes */
      className: cx('fp-button fp-custom-appearance text-label-2', className),
      'data-full-width': fullWidth,
      'data-loading': loading,
      'data-size': size,
      'data-testid': testID,
      'data-variant': variant,
      tabIndex: 0,
    };
    const content = (
      <>
        {getContent()}
        <div className={cx('absolute', loading ? 'visible' : 'invisible')} aria-hidden={!loading}>
          <LoadingSpinner ariaLabel={loadingAriaLabel} color={variant === 'primary' ? 'quinary' : 'tertiary'} />
        </div>
      </>
    );

    if (href) {
      return (
        <a
          {...(props as AnchorHTMLAttributes<HTMLAnchorElement>)}
          {...sharedProps}
          aria-disabled={disabled || loading || undefined}
          // An anchor has no `disabled` attribute; dropping the href is what disarms it.
          href={disabled || loading ? undefined : href}
          ref={ref as Ref<HTMLAnchorElement>}
          rel={target === '_blank' ? 'noopener noreferrer' : undefined}
          // Leaves the tab order while disarmed, matching what `disabled` does to the button.
          tabIndex={disabled || loading ? -1 : 0}
          target={target}
        >
          {content}
        </a>
      );
    }

    return (
      <button
        {...(props as ButtonHTMLAttributes<HTMLButtonElement>)}
        {...sharedProps}
        disabled={disabled}
        form={form}
        ref={ref as Ref<HTMLButtonElement>}
        type={type}
      >
        {content}
      </button>
    );
  },
);

export default Button;
